import { useEscrowNotificationHandlers } from 'src/containers/Escrow/Technical';
import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { EscrowPartyTypeEnum } from 'src/graphql/schema';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { getEscrowActivityNotificationOptions } from 'src/shared/entities/escrow-activity-notification-options';
import { disruptedNotificationsOptionsRich, gracePeriodOptions } from 'src/shared/entities/escrow-notification-options';
import {
  PartyNotificationSettings,
  ActivityNotifications,
  DisruptedAccessNotifications,
} from 'src/components/Escrow/Notifications';

const { activityNotificationOptionsRich } = getEscrowActivityNotificationOptions('software');

const NotificationSettingsPage = () => {
  const {
    softwareEscrow,
    softwareEscrow: { id: escrowId, currentCompanyRole, startedBy, beneficiarySharedInformation, app },
  } = useSoftwareEscrow();
  const { onActivityNotificationChange, onDisruptionNotificationChange, onGracePeriodChange } =
    useEscrowNotificationHandlers({
      escrowId,
      currentCompanyRole: currentCompanyRole!,
    });

  const isInvitedParty = currentCompanyRole !== startedBy;
  const notificationSettings = {
    activityNotification:
      currentCompanyRole === EscrowPartyTypeEnum.Depositor
        ? softwareEscrow.depositorActivityNotification
        : softwareEscrow.beneficiaryActivityNotification,
    disruptionNotification:
      currentCompanyRole === EscrowPartyTypeEnum.Depositor
        ? softwareEscrow.depositorDisruptionNotification
        : softwareEscrow.beneficiaryDisruptionNotification,
    disruptionNotificationGracePeriod:
      (currentCompanyRole === EscrowPartyTypeEnum.Depositor
        ? softwareEscrow.depositorDisruptionNotificationGracePeriod
        : softwareEscrow.beneficiaryDisruptionNotificationGracePeriod) ?? undefined,
  };

  return (
    <SoftwareOnboardingMain
      subtitle={`Technical information: Notification settings for the ${currentCompanyRole}`}
      title={
        currentCompanyRole === EscrowPartyTypeEnum.Depositor
          ? "Manage the information and the notifications you'll receive."
          : 'Manage the notifications you’ll receive.'
      }
      description="Note: You can edit this later under Settings → Technical Information."
    >
      <div className="max-w-260 w-full mx-auto">
        {isInvitedParty && !beneficiarySharedInformation ? (
          <Card className="bg-[--alpha(var(--muted)/60%)] w-full max-w-145 mx-auto">
            <CardHeader>
              <CardTitle>Activity notifications</CardTitle>
              <CardDescription className="text-foreground text-xs">
                You’ll receive an escrow activation email but no other notifications or information will be shared with
                you.
                <br />
                <br /> To request asset visibility, go to Settings and submit a change request after completing
                onboarding.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <PartyNotificationSettings
            activityNotificationNode={
              <ActivityNotifications
                description={`The notification types delivered to everyone with access to ${app.name}. Information about deposited assets includes names and deposit dates.`}
                value={notificationSettings.activityNotification}
                options={activityNotificationOptionsRich}
                onChange={onActivityNotificationChange}
              />
            }
            disruptedAccessNotificationNode={
              <DisruptedAccessNotifications
                description="The notification type delivered if access to an asset is disrupted, and the asset can no longer be deposited."
                values={{
                  disruptionNotification: notificationSettings.disruptionNotification,
                  disruptionNotificationGracePeriod: notificationSettings.disruptionNotificationGracePeriod ?? null,
                }}
                options={{
                  disruptionNotificationOptions: disruptedNotificationsOptionsRich,
                  gracePeriodOptions: gracePeriodOptions,
                }}
                onDisruptionNotificationChange={onDisruptionNotificationChange}
                onDisruptionNotificationGracePeriodChange={onGracePeriodChange}
                graceNote="We’ll keep retrying the deposit during this period before notifying you that something’s wrong."
                graceLabel="Disruption grace period"
              />
            }
          />
        )}
      </div>
    </SoftwareOnboardingMain>
  );
};

export default NotificationSettingsPage;
